/**
 * Active Task selector helpers for Closing Package (FE-004 / FE-005B).
 * Task Engine remains SoT — this module only filters/display-selects.
 * Whitelist = canonical CP workflow tasks (IMP-001G/H), plus known aliases.
 */

import { resolveClosingPackageCanonicalTaskType } from '../workspace/closingPackageTaskTypes';

import { isActiveControlTaskStatus as isActiveControlTaskStatusShared } from '../../../constants/controlTaskStatuses';

export { isActiveControlTaskStatusShared as isActiveControlTaskStatus };

export const CLOSING_PACKAGE_TASK_TYPES = [
  'FORM_CLOSING_PACKAGE',
  'SEND_SCANS_TO_CLIENT',
  'WAIT_CLIENT_APPROVAL',
  'REVISE_CLOSING_PACKAGE',
  'PREPARE_ORIGINALS',
  'SEND_ORIGINALS',
  'CONFIRM_CLIENT_RECEIPT',
  'SEND_VIA_EDO',
  'CONFIRM_EDO_RECEIPT',
] as const;

export type ClosingPackageActiveTaskType = (typeof CLOSING_PACKAGE_TASK_TYPES)[number];

export type ClosingPackageTaskLike = {
  id: number;
  taskType: string;
  status: string;
  applicationId?: number | null;
  dueDate?: string | null;
};

export function isClosingPackageTaskType(taskType: string | null | undefined): boolean {
  return resolveClosingPackageCanonicalTaskType(taskType) != null;
}

export function filterClosingPackageTasks<T extends ClosingPackageTaskLike>(tasks: T[]): T[] {
  return tasks.filter(
    (task) =>
      isClosingPackageTaskType(task.taskType) && isActiveControlTaskStatusShared(task.status)
  );
}

/**
 * Pick primary active task for Summary / Active Task widget.
 * Prefer earliest dueDate; otherwise lowest id.
 */
export function selectPrimaryClosingPackageTask<T extends ClosingPackageTaskLike>(
  tasks: T[]
): T | null {
  const filtered = filterClosingPackageTasks(tasks);
  if (filtered.length === 0) return null;

  return [...filtered].sort((a, b) => {
    const aDue = a.dueDate ? Date.parse(a.dueDate) : Number.POSITIVE_INFINITY;
    const bDue = b.dueDate ? Date.parse(b.dueDate) : Number.POSITIVE_INFINITY;
    if (aDue !== bDue) return aDue - bDue;
    return a.id - b.id;
  })[0];
}

export function countRemainingClosingPackageTasks<T extends ClosingPackageTaskLike>(
  tasks: T[],
  primaryId?: number | null
): number {
  const filtered = filterClosingPackageTasks(tasks);
  if (primaryId == null) return Math.max(0, filtered.length - (filtered.length > 0 ? 1 : 0));
  return filtered.filter((task) => task.id !== primaryId).length;
}
